import { useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import z from "zod";

export const Route = createFileRoute("/_auth/accept-token/")({
  validateSearch: z.object({
    token: z.string(),
    redirect: z.string().optional(),
  }),
  component: AcceptTokenPage,
});

function AcceptTokenPage() {
  const [error, setError] = useState<string | null>(null);
  const search = useSearch({ from: "/_auth/accept-token/" });
  const token = (search?.token as string | undefined) ?? undefined;
  const redirect =
    (search?.redirect as string | undefined) ?? "/settings/password";

  const { client, setActive } = useClerk();
  const { isSignedIn } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // If the user is already signed in, just go to the redirect target
      if (isSignedIn && redirect) {
        window.location.replace(redirect);
        return;
      }

      if (!token || !client) return;
      try {
        const attempt = await client.signIn.create({
          strategy: "ticket",
          ticket: token,
        });

        if (attempt.status === "complete" && attempt.createdSessionId) {
          await setActive({ session: attempt.createdSessionId });
          if (!cancelled) {
            window.location.replace(redirect);
          }
          return;
        }
        setError("Unable to complete sign-in. Please try again.");
      } catch {
        setError("Invalid or expired link. Please request a new one.");
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, client, setActive, redirect, isSignedIn]);

  if (!token) {
    return <div className="mx-auto max-w-md p-8">No token provided.</div>;
  }

  if (error) {
    return <div className="mx-auto max-w-md p-8 text-red-600">{error}</div>;
  }

  return <div className="mx-auto max-w-md p-8">Signing you in…</div>;
}

export default AcceptTokenPage;
